import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import '../Main.scss';

import { 
    PortalSafeDataCollectionLimitResponse,
    PortalTextDataCollectionLimitDetails,
} from '../../Models/Api/strongbox.financialportal';

import { ApplicationState } from '../../Store';
import { 
    GetDataCollectionLimits,
} from '../../Store/Tenant';
import {
    actionCreators as UIStateActions,
    GetPortalBusyState,
} from '../../Store/UIState';
import {
    GetPortalText,
} from '../../Store/PortalAssets';
import {
    StringFormat,
} from '../../Utils/StringUtils';

type InjectedReduxState = {
    dataCollectionLimits?: PortalSafeDataCollectionLimitResponse;
    limitsText?: PortalTextDataCollectionLimitDetails;
    portalBusy: boolean;
};

type InjectedActionCreators = typeof UIStateActions;

type DataCollectionLimitsSummaryProps = {
};

type Props = InjectedReduxState & InjectedActionCreators & DataCollectionLimitsSummaryProps;

export const DataCollectionLimitSummaryComponent: React.FC<Props> = (props): React.ReactElement => {
    const {
        limitsText,
        dataCollectionLimits,
        portalBusy,
        SetShowDataCollectionLimitsDetails,
    } = props;

    const hasRemainingConnections = React.useMemo(() => {
        if (!dataCollectionLimits || !dataCollectionLimits.hasLimit) {
            return true;
        }
        return dataCollectionLimits.countRemaining! > 0;
    }, [dataCollectionLimits]);

    const showDataCollectionDetails = (): void => {
        if (!portalBusy) {
            SetShowDataCollectionLimitsDetails(true);
        }
    }

    const summaryText: React.ReactElement = React.useMemo(() => {
        // This shouldn't happen, the consumer shouldn't be using the control unless there's a limit, but hey...
        if (!dataCollectionLimits || !dataCollectionLimits.hasLimit) {
            return (<></>);
        }
        if (!limitsText) {
            return (<></>);
        }
        if (hasRemainingConnections) {
            const fullSummary = StringFormat(limitsText?.templateDataXOfYDataCollectionsRemaining || '{0}{1}', dataCollectionLimits.countRemaining, dataCollectionLimits.limit);
            const shortenedSummary = StringFormat(limitsText?.templateDataXOfYDataCollectionsRemainingShortened || '{0}{1}', dataCollectionLimits.countRemaining, dataCollectionLimits.limit);

            return (
                <React.Fragment>
                    <span
                        className={'percent-complete percent-complete-full'}
                        onClick={showDataCollectionDetails}
                    >
                        {fullSummary}
                    </span>
                    <span
                        className={'percent-complete percent-complete-shortened'}
                        onClick={showDataCollectionDetails}
                    >
                        {shortenedSummary}
                    </span>
                </React.Fragment>
            );
        } else {
            return (
                <span
                    className={'percent-complete'}
                    onClick={showDataCollectionDetails}
                >
                    {limitsText?.noCollectionsRemaining || ''}
                </span>
            );
        }
        
        // These are the only dependencies I want.
        // eslint-disable-next-line react-hooks/exhaustive-deps 
    }, [limitsText, dataCollectionLimits, hasRemainingConnections])

    const widthValue = React.useMemo(() => {
        if (!dataCollectionLimits || !dataCollectionLimits.hasLimit) {
            return '0%';
        }

        // Handle the weird cases where somehow count remaining is less than 0 or greater than the limit.
        return `${Math.min(100 * (Math.max(0, dataCollectionLimits.countRemaining!) / dataCollectionLimits.limit!), 100)}%`;
    }, [dataCollectionLimits])

    const mainDivClass = React.useMemo(()=> {
        let result = 'header-text-container';
        if (!summaryText) {
            return result;
        }
        if (!portalBusy) {
            result += ' header-text-container-with-click';
        }
        if (!hasRemainingConnections) {
            result += ' header-text-container-full';
        }

        return result;
    }, [summaryText, portalBusy, hasRemainingConnections])

    // Sanity check.
    
    if ((!dataCollectionLimits) || (!dataCollectionLimits.hasLimit) || (!summaryText)) {
        return (<></>);
    }

    return (
        <div 
            className={mainDivClass}
            onClick={showDataCollectionDetails}
        >
            <React.Fragment>
                <div 
                    className={'bar-div'}
                    style={{ width: widthValue }}
                    onClick={showDataCollectionDetails}
                />
                {summaryText}
            </React.Fragment>
        </div>
    );
}

export const DataCollectionLimitSummary = connect<InjectedReduxState, InjectedActionCreators, DataCollectionLimitsSummaryProps, ApplicationState>(
    (appState: ApplicationState, props: DataCollectionLimitsSummaryProps) => {
        const result: InjectedReduxState = {
            dataCollectionLimits: GetDataCollectionLimits(appState),
            limitsText: GetPortalText<PortalTextDataCollectionLimitDetails>(appState, 'dataCollectionLimitDetails'),
            portalBusy: GetPortalBusyState(appState),
        };

        return result;
    },
    dispatch => bindActionCreators(
        {
            ...UIStateActions,
        },
        dispatch
    )
)(DataCollectionLimitSummaryComponent);

